import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { toast } from '@/components/ui';
import { useSession } from '@/features/auth/api/auth.api';
import { apiClient } from '@/lib/api-client';
import type { ApiError } from '@/lib/api-error';
import { useExportsStore } from '@/stores/exports.store';
import { ExportStatus, type ExportDataset } from '@/types/domain';

/**
 * Starting a background export, watching it, and saving the file it produces.
 *
 * The one piece of polling in the app: a job is written off the request thread
 * on the server, so the client asks again every couple of seconds until it is
 * READY - and only while something is actually building, so an idle tray costs
 * nothing.
 */

const BASE = '/workspace/exports';

/** How often to ask again while a job is still building. */
const POLL_INTERVAL_MS = 2000;

export interface ExportJobView {
  id: string;
  dataset: ExportDataset;
  datasetLabel: string;
  status: ExportStatus;
  fileName: string;
  rowCount: number;
  byteSize: number;
  error: string;
  createdAt: string;
  finishedAt: string | null;
}

/**
 * The filters a page hands its export, as that page's list has them.
 *
 * All optional; only the ones the dataset understands are read on the server.
 * Dates go as ISO strings, which is how the list pages already hold them.
 */
export interface ExportFilters {
  search?: string;
  status?: string;
  expiringOnly?: boolean;
  stageKey?: string;
  outcome?: string;
  sourceKey?: string;
  ownerId?: string;
  followUpDueOnly?: boolean;
  kind?: string;
  leadId?: string;
  customerId?: string;
  outstandingOnly?: boolean;
  overdueOnly?: boolean;
  serviceId?: string;
  assigneeId?: string;
  flowId?: string;
  state?: string;
  accountId?: string;
  category?: string;
  partyType?: string;
  partyId?: string;
  from?: string;
  to?: string;
}

export interface StartExportInput {
  dataset: ExportDataset;
  filters?: ExportFilters;
}

export const exportKeys = {
  all: () => ['exports'] as const,
  list: () => [...exportKeys.all(), 'list'] as const,
};

/** True while a job is still going - what drives whether we keep polling. */
export function isActive(job: ExportJobView): boolean {
  return job.status === ExportStatus.QUEUED || job.status === ExportStatus.PROCESSING;
}

/**
 * This person's recent exports, polled while any is still building.
 *
 * `refetchInterval` returns false the moment nothing is active, so the tray is
 * not asking the server a question whose answer cannot change. Starting an
 * export invalidates this query, which brings a QUEUED job in and restarts the
 * polling on its own.
 */
export function useExportJobs() {
  const { data: session } = useSession();

  return useQuery<ExportJobView[]>({
    queryKey: exportKeys.list(),
    queryFn: () => apiClient.get<ExportJobView[]>(BASE),
    enabled: Boolean(session),
    refetchInterval: (query) =>
      (query.state.data ?? []).some(isActive) ? POLL_INTERVAL_MS : false,
  });
}

export function useStartExport(): UseMutationResult<ExportJobView, ApiError, StartExportInput> {
  const queryClient = useQueryClient();
  const track = useExportsStore((state) => state.track);

  return useMutation<ExportJobView, ApiError, StartExportInput>({
    mutationFn: (input) => apiClient.post<ExportJobView>(BASE, input),
    onSuccess: (job) => {
      track(job.id);
      void queryClient.invalidateQueries({ queryKey: exportKeys.list() });
      toast.info(
        'Preparing your export',
        `${job.datasetLabel} — we'll offer to save it the moment it's ready.`,
      );
    },
    onError: (error) => {
      toast.error('Could not start the export', error.message);
    },
  });
}

/** Clears a finished job from the database - the last step once it is saved. */
export function useClearExport(): UseMutationResult<{ deleted: boolean }, ApiError, string> {
  const queryClient = useQueryClient();
  const untrack = useExportsStore((state) => state.untrack);

  return useMutation<{ deleted: boolean }, ApiError, string>({
    mutationFn: (id) => apiClient.delete<{ deleted: boolean }>(`${BASE}/${id}`),
    onSuccess: (_result, id) => {
      untrack(id);
      void queryClient.invalidateQueries({ queryKey: exportKeys.list() });
    },
  });
}

/**
 * Pulls the finished workbook down and hands it to the browser's save dialog.
 *
 * The bytes come over `getBlob` so the session cookie rides along - a plain
 * link would be a navigation the server might answer with a 401 the user never
 * sees. The blob URL is built, clicked and revoked here; this is the one
 * save-as in the app, and it exists because an export is a file somebody keeps,
 * not a page they look at.
 */
export async function saveExportFile(job: ExportJobView): Promise<void> {
  const blob = await apiClient.getBlob(`${BASE}/${job.id}/download`);

  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = job.fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
